import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { unwrapPage } from "@/lib/paging";
import { formatINRCompact } from "@/lib/utils";
import type { DealRecord, PipelineStageRecord } from "@/types";
import { dateLocale, withClock } from "@/lib/dateTime";

const fmtDate = (d?: string) => (d ? new Date(`${d.slice(0, 10)}T00:00:00`).toLocaleDateString(dateLocale(), { day: "2-digit", month: "short" }) : "—");
const fmtWhen = (d: string) => {
  const t = new Date(d);
  const days = Math.floor((Date.now() - t.getTime()) / 86400000);
  if (days < 1) return t.toLocaleTimeString(dateLocale(), withClock({ hour: "2-digit", minute: "2-digit" }));
  return days === 1 ? "Yesterday" : t.toLocaleDateString(dateLocale(), { day: "2-digit", month: "short" });
};
const ACTIVITY_DOT: Record<string, string> = { CALL: "bg-primary", MEETING: "bg-primary", EMAIL: "bg-info", WHATSAPP: "bg-success" };

/** The selected deal, docked beside the board: what matters now, the next step and the latest activity. */
export function DealQuickView({
  deal,
  nextStage,
  onClose,
  onMoveNext,
  onDelete,
}: {
  deal: DealRecord;
  nextStage: PipelineStageRecord | null;
  onClose: () => void;
  onMoveNext: (d: DealRecord, s: PipelineStageRecord) => void;
  onDelete: (d: DealRecord) => void;
}) {
  const navigate = useNavigate();
  const [recent, setRecent] = useState<{ id: string; activity_type: string; title: string; created_on: string }[]>([]);

  useEffect(() => {
    let alive = true;
    setRecent([]);
    api
      .get(`/api/DealActivity/ListByDeal/${deal.id}?page_size=3`)
      .then((r) => alive && setRecent(unwrapPage<any>(r).rows.slice(0, 3)))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [deal.id]);

  const signal = deal.buying_signal;
  const open = deal.deal_status === "OPEN";
  const stageLine = [deal.stage?.name, open && deal.days_in_stage !== undefined ? `${deal.days_in_stage} days in stage` : null].filter(Boolean).join(" · ");

  return (
    <aside aria-label="Selected deal" className="flex w-[320px] shrink-0 flex-col gap-3.5 self-stretch overflow-y-auto border-l border-border bg-card p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <span className="text-[11.5px] text-muted-foreground">{[deal.deal_number, stageLine].filter(Boolean).join(" · ")}</span>
          <h2 className="mt-0.5 text-[17px] font-semibold leading-snug">{deal.name}</h2>
          <button type="button" onClick={() => deal.account_id && navigate(`/accounts/${deal.account_id}`)} className="text-[12.5px] text-primary hover:underline">
            {deal.account?.name || "Independent account"}
          </button>
        </div>
        <Button variant="outline" size="icon-sm" aria-label="Close panel" onClick={onClose}>
          <X className="size-4" />
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-lg bg-panel px-2.5 py-2">
          <div className="text-[11px] text-muted-foreground">Amount</div>
          <div className="font-mono text-[15px] font-semibold">{formatINRCompact(deal.amount)}</div>
        </div>
        <div className="rounded-lg bg-panel px-2.5 py-2">
          <div className="text-[11px] text-muted-foreground">Close date</div>
          <div className="font-mono text-[15px] font-semibold">{fmtDate(deal.expected_close_date)}</div>
        </div>
      </div>

      {signal && (
        <div className="rounded-lg bg-gold-soft px-3 py-2.5">
          <div className="font-semibold text-gold-ink">Buying signal</div>
          <div className="text-[12.5px] text-gold-ink">
            Proposal opened {signal.views} {signal.views === 1 ? "time" : "times"} today{signal.seconds >= 60 ? `; ${Math.round(signal.seconds / 60)} min reading` : ""}.
          </div>
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Next step</h3>
        {deal.next_step ? (
          <div className="flex items-center justify-between gap-2 rounded-lg border border-border px-2.5 py-2">
            <span className="min-w-0 flex-1 truncate">{deal.next_step.title}</span>
            <span className="font-mono text-xs text-primary">{fmtWhen(deal.next_step.due_at)}</span>
          </div>
        ) : (
          <div className={`rounded-lg border border-dashed border-border px-2.5 py-2 text-[12.5px] ${open ? "text-warning" : "text-muted-foreground"}`}>{open ? "No next step. Add a task from the deal." : "—"}</div>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Recent</h3>
        {recent.length === 0 ? (
          <span className="text-[12.5px] text-muted-foreground">No activity yet.</span>
        ) : (
          recent.map((a) => (
            <div key={a.id} className="flex items-start gap-2 text-[12.5px]">
              <span className={`mt-[7px] size-1.5 shrink-0 rounded-full ${ACTIVITY_DOT[a.activity_type] || "bg-muted-foreground"}`} />
              <span className="min-w-0 flex-1 truncate">{a.title || a.activity_type}</span>
              <span className="whitespace-nowrap text-muted-foreground">{fmtWhen(a.created_on)}</span>
            </div>
          ))
        )}
      </div>

      <div className="mt-auto flex flex-col gap-2 pt-2">
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={() => navigate(`/deals/${deal.id}`)}>
            Open deal
          </Button>
          {open && nextStage && (
            <Button className="flex-1 bg-sidebar text-white hover:bg-sidebar/90" onClick={() => onMoveNext(deal, nextStage)}>
              To {nextStage.name}
            </Button>
          )}
        </div>
        <button type="button" onClick={() => onDelete(deal)} className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-danger">
          <Trash2 className="size-3.5" /> Delete deal
        </button>
      </div>
    </aside>
  );
}
